import { ANNUAL_BONUS_PLAN_CODE } from './catalogData';
import type { BenefitCategoryCode } from './categories';
import { validateBenefitPlanShape, type FieldIssue } from './rules';
import {
  COVERAGE_LEVELS, type BenefitCatalogItem, type BenefitPlan, type BenefitPlanInput, type ContributionMethod, type CoverageLevel, type EnrollmentMethod,
  type FundingModel, type PayrollFrequency,
} from './types';

/**
 * The benefit-plan form: text-friendly state (every number is a string while the person types), converted to the API's input on submit. Hidden fields
 * are sent as null, so a plan never carries an amount its funding model or contribution method does not use.
 */

export interface TierState {
  coverageLevel: CoverageLevel;
  employeeAmount: string;
  employerAmount: string;
}

export interface PlanFormState {
  name: string;
  shortCode: string;
  categoryCode: BenefitCategoryCode;
  /** Empty means a custom benefit. */
  typeCode: string;
  description: string;
  providerName: string;
  planYear: string;
  effectiveStartDate: string;
  effectiveEndDate: string;
  eligibilityDescription: string;
  waitingPeriodDays: string;
  enrollmentMethod: EnrollmentMethod;
  fundingModel: FundingModel;
  contributionMethod: ContributionMethod;
  employeeContributionAmount: string;
  employerContributionAmount: string;
  employeeContributionPercent: string;
  employerContributionPercent: string;
  tiers: TierState[];
  payrollFrequency: PayrollFrequency;
  associatedDeductionCodeId: string;
  associatedEmployerContributionCodeId: string;
  taxProfileCode: string;
  w2ReportingCode: string;
  planDocumentReference: string;
  employeeVisibleDescription: string;
  internalNotes: string;
  isActive: boolean;
}

export function emptyPlanForm(category: BenefitCategoryCode): PlanFormState {
  const leave = category === 'leave-wellbeing';
  return {
    name: '', shortCode: '', categoryCode: category, typeCode: '', description: '', providerName: '', planYear: '', effectiveStartDate: '', effectiveEndDate: '',
    eligibilityDescription: '', waitingPeriodDays: '', enrollmentMethod: leave ? 'NotApplicable' : 'AdminEnrolled', fundingModel: leave ? 'NotApplicable' : 'EmployerPaid',
    contributionMethod: 'NoPayrollContribution', employeeContributionAmount: '', employerContributionAmount: '', employeeContributionPercent: '', employerContributionPercent: '',
    tiers: [], payrollFrequency: 'EveryPayroll', associatedDeductionCodeId: '', associatedEmployerContributionCodeId: '', taxProfileCode: '', w2ReportingCode: '',
    planDocumentReference: '', employeeVisibleDescription: '', internalNotes: '', isActive: true,
  };
}

const text = (value: string | null): string => value ?? '';
const numText = (value: number | null): string => (value === null ? '' : String(value));

export function planToForm(plan: BenefitPlan): PlanFormState {
  return {
    name: plan.name, shortCode: plan.shortCode, categoryCode: plan.categoryCode, typeCode: text(plan.typeCode), description: text(plan.description),
    providerName: text(plan.providerName), planYear: numText(plan.planYear), effectiveStartDate: text(plan.effectiveStartDate), effectiveEndDate: text(plan.effectiveEndDate),
    eligibilityDescription: text(plan.eligibilityDescription), waitingPeriodDays: numText(plan.waitingPeriodDays), enrollmentMethod: plan.enrollmentMethod,
    fundingModel: plan.fundingModel, contributionMethod: plan.contributionMethod, employeeContributionAmount: numText(plan.employeeContributionAmount),
    employerContributionAmount: numText(plan.employerContributionAmount), employeeContributionPercent: numText(plan.employeeContributionPercent),
    employerContributionPercent: numText(plan.employerContributionPercent),
    tiers: plan.coverageTiers.map((t) => ({ coverageLevel: t.coverageLevel, employeeAmount: numText(t.employeeAmount), employerAmount: numText(t.employerAmount) })),
    payrollFrequency: plan.payrollFrequency, associatedDeductionCodeId: text(plan.associatedDeductionCodeId),
    associatedEmployerContributionCodeId: text(plan.associatedEmployerContributionCodeId), taxProfileCode: text(plan.taxProfileCode), w2ReportingCode: text(plan.w2ReportingCode),
    planDocumentReference: text(plan.planDocumentReference), employeeVisibleDescription: text(plan.employeeVisibleDescription), internalNotes: text(plan.internalNotes),
    isActive: plan.isActive,
  };
}

/** Prefill from a catalog type: its name and description, and a sensible starting funding model and contribution method from what the type usually does.
 * The person can change any of it. A leave type is always "no payroll contribution", and so is the Annual Bonus Plan. */
export function applyCatalogType(form: PlanFormState, item: BenefitCatalogItem): PlanFormState {
  const base: PlanFormState = { ...form, typeCode: item.code, categoryCode: item.categoryCode, name: item.name, description: item.description, tiers: [] };
  if (item.leaveLinked) return { ...base, fundingModel: 'NotApplicable', contributionMethod: 'NoPayrollContribution', enrollmentMethod: 'NotApplicable' };
  if (item.code === ANNUAL_BONUS_PLAN_CODE || item.createsPayrollDeduction === 'Never') return { ...base, fundingModel: 'EmployerPaid', contributionMethod: 'NoPayrollContribution' };
  if (item.createsPayrollDeduction === 'Typical') return { ...base, fundingModel: 'EmployeePaid', contributionMethod: 'FixedPerPayroll', enrollmentMethod: 'EmployeeElected' };
  return { ...base, fundingModel: 'EmployerPaid', contributionMethod: 'NoPayrollContribution' };
}

/** A starting short code from a name: upper-case letters and digits joined by hyphens, at most 30 characters. The person can edit it. */
export function suggestShortCode(name: string): string {
  const code = name.toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30).replace(/-+$/g, '');
  return code.length >= 2 ? code : '';
}

export function usesFlatAmounts(method: ContributionMethod): boolean {
  return method === 'FixedPerPayroll' || method === 'FixedPerMonth';
}

export function usesPercents(method: ContributionMethod): boolean {
  return method === 'PercentOfEligiblePay' || method === 'EmployerPaidPercent' || method === 'EmployeePaidPercent';
}

/** Which of the four amount fields apply, from the funding model and contribution method. */
export function visibleAmountFields(form: Pick<PlanFormState, 'fundingModel' | 'contributionMethod'>): {
  employeeAmount: boolean; employerAmount: boolean; employeePercent: boolean; employerPercent: boolean; tiers: boolean; employeeTier: boolean; employerTier: boolean;
} {
  const { fundingModel: funding, contributionMethod: method } = form;
  const employee = funding !== 'EmployerPaid';
  const employer = funding !== 'EmployeePaid';
  return {
    employeeAmount: usesFlatAmounts(method) && employee,
    employerAmount: usesFlatAmounts(method) && employer,
    employeePercent: employee && (method === 'PercentOfEligiblePay' || method === 'EmployeePaidPercent'),
    employerPercent: employer && (method === 'PercentOfEligiblePay' || method === 'EmployerPaidPercent'),
    tiers: method === 'TieredByCoverage',
    employeeTier: method === 'TieredByCoverage' && employee,
    employerTier: method === 'TieredByCoverage' && employer,
  };
}

function parseNumber(raw: string, field: string, issues: FieldIssue[]): number | null {
  const t = raw.trim();
  if (t === '') return null;
  if (!/^-?\d*\.?\d+$/.test(t)) {
    issues.push({ field, message: 'Enter a number, for example 125.50.' });
    return null;
  }
  return Number(t);
}

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

export function formToInput(form: PlanFormState): { input: BenefitPlanInput; issues: FieldIssue[] } {
  const parseIssues: FieldIssue[] = [];
  const show = visibleAmountFields(form);
  const method = form.contributionMethod;
  const noContribution = method === 'NoPayrollContribution';

  const tiers = show.tiers
    ? form.tiers.map((t, i) => ({
      coverageLevel: t.coverageLevel,
      employeeAmount: show.employeeTier ? parseNumber(t.employeeAmount, `coverageTiers.${i}`, parseIssues) : null,
      employerAmount: show.employerTier ? parseNumber(t.employerAmount, `coverageTiers.${i}`, parseIssues) : null,
    }))
    : [];

  const input: BenefitPlanInput = {
    name: form.name.trim(),
    shortCode: form.shortCode.trim(),
    categoryCode: form.categoryCode,
    typeCode: orNull(form.typeCode),
    description: orNull(form.description),
    providerName: orNull(form.providerName),
    planYear: parseNumber(form.planYear, 'planYear', parseIssues),
    effectiveStartDate: orNull(form.effectiveStartDate),
    effectiveEndDate: orNull(form.effectiveEndDate),
    eligibilityDescription: orNull(form.eligibilityDescription),
    waitingPeriodDays: parseNumber(form.waitingPeriodDays, 'waitingPeriodDays', parseIssues),
    enrollmentMethod: form.enrollmentMethod,
    fundingModel: form.fundingModel,
    contributionMethod: method,
    employeeContributionAmount: !noContribution && show.employeeAmount ? parseNumber(form.employeeContributionAmount, 'employeeContributionAmount', parseIssues) : null,
    employerContributionAmount: !noContribution && show.employerAmount ? parseNumber(form.employerContributionAmount, 'employerContributionAmount', parseIssues) : null,
    employeeContributionPercent: !noContribution && show.employeePercent ? parseNumber(form.employeeContributionPercent, 'employeeContributionPercent', parseIssues) : null,
    employerContributionPercent: !noContribution && show.employerPercent ? parseNumber(form.employerContributionPercent, 'employerContributionPercent', parseIssues) : null,
    coverageTiers: tiers,
    payrollFrequency: form.payrollFrequency,
    associatedDeductionCodeId: orNull(form.associatedDeductionCodeId),
    associatedEmployerContributionCodeId: orNull(form.associatedEmployerContributionCodeId),
    taxProfileCode: noContribution ? null : orNull(form.taxProfileCode),
    w2ReportingCode: orNull(form.w2ReportingCode),
    planDocumentReference: orNull(form.planDocumentReference),
    employeeVisibleDescription: orNull(form.employeeVisibleDescription),
    internalNotes: orNull(form.internalNotes),
    isActive: form.isActive,
  };
  // Parse problems come first (a field that is not a number cannot be range-checked); the shape rules then add what the contract requires.
  const parsed = new Set(parseIssues.map((i) => i.field));
  const ruleIssues = validateBenefitPlanShape(input).filter((i) => !parsed.has(i.field));
  return { input, issues: [...parseIssues, ...ruleIssues] };
}

export const COVERAGE_LEVEL_CHOICES: CoverageLevel[] = COVERAGE_LEVELS.filter((c) => c !== 'Waived');
